import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Upload, Plus, AlertTriangle, X, FileText } from "lucide-react";

function estimateWorkingDays(s, e) {
  if (!s || !e) return 0;
  try {
    const d0 = new Date(s);
    const d1 = new Date(e);
    if (isNaN(d0) || isNaN(d1) || d1 < d0) return 0;
    let days = 0;
    const cur = new Date(d0);
    while (cur <= d1) {
      const wd = cur.getDay();
      if (wd !== 0 && wd !== 6) days += 1;
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  } catch { return 0; }
}

/**
 * Dialog standalone pentru upload Meldeverfahren cu detecție multi-angajat.
 * Folosește endpoint-ul global `/meldungen/preview-upload` (fără pid).
 *
 * Props:
 *   open, onOpenChange  — controlul dialogului
 *   onSaved             — callback după bulk-confirm reușit (primește răspunsul)
 *   persons             — lista de persoane (din /persons-overview) pentru dropdown
 *                         dacă nu e dată, componenta o încarcă singură
 */
export default function MeldungBulkUploadDialog({ open, onOpenChange, onSaved, persons }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [internalPersons, setInternalPersons] = useState([]);

  // Load persons if not provided
  useEffect(() => {
    if (!open) return;
    if (persons && persons.length > 0) {
      setInternalPersons(persons);
      return;
    }
    api.get("/persons-overview").then(({ data }) => setInternalPersons(data || [])).catch(() => {});
  }, [open, persons]);

  const personOptions = useMemo(() => {
    return (internalPersons || [])
      .filter((p) => p.active !== false)
      .map((p) => ({
        id: p.id,
        label: `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)",
        is_sub: !!p.is_subcontractor,
      }))
      .sort((a, b) => {
        if (a.is_sub !== b.is_sub) return a.is_sub ? -1 : 1;
        return a.label.localeCompare(b.label, "ro");
      });
  }, [internalPersons]);

  // Auto-open file picker when dialog opens
  useEffect(() => {
    if (open && !preview && !uploading) {
      const t = setTimeout(() => fileInputRef.current?.click(), 150);
      return () => clearTimeout(t);
    }
  }, [open, preview, uploading]);

  const reset = () => { setPreview(null); setRows([]); };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      // user cancelled file picker; close dialog if no preview yet
      if (!preview) onOpenChange?.(false);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/meldungen/preview-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      const initial = (data.entries || []).map((en, idx) => ({
        __key: `${idx}`,
        __skip: false,
        person_id: en.matched_person_id || null,
        matched_person_name: en.matched_person_name,
        employee_name: en.employee_name || "",
        start_date: en.start_date || "",
        end_date: en.end_date || "",
        working_days: en.suggested_working_days || 0,
        note: "",
      }));
      if (initial.length === 0) {
        initial.push({
          __key: "0",
          __skip: false,
          person_id: null,
          matched_person_name: null,
          employee_name: "",
          start_date: "",
          end_date: "",
          working_days: 0,
          note: "",
        });
      }
      setRows(initial);
      if (data.extract_error) {
        toast.warning("OCR eșuat. Completează manual.");
      } else if ((data.entries || []).length === 0) {
        toast.warning("Niciun angajat detectat. Completează manual.");
      } else {
        const matched = (data.entries || []).filter((en) => en.matched_person_id).length;
        toast.success(`${data.entries.length} angajați detectați (${matched} potriviți automat)`);
      }
    } catch (err) {
      const d = err?.response?.data;
      const msg = (d && (d.detail || d.message)) || err?.message || "Eroare la upload";
      toast.error(`Eroare la upload: ${msg}`);
      onOpenChange?.(false);
    } finally {
      setUploading(false);
    }
  };

  const updateRow = (key, field, value) => {
    setRows((rs) => rs.map((r) => {
      if (r.__key !== key) return r;
      const next = { ...r, [field]: value };
      if (field === "start_date" || field === "end_date") {
        next.working_days = estimateWorkingDays(
          field === "start_date" ? value : next.start_date,
          field === "end_date" ? value : next.end_date,
        );
      }
      return next;
    }));
  };

  const toggleSkip = (key) => setRows((rs) => rs.map((r) => r.__key === key ? { ...r, __skip: !r.__skip } : r));
  const addEmptyRow = () => setRows((rs) => [
    ...rs,
    { __key: `${Date.now()}-${rs.length}`, __skip: false, person_id: null, matched_person_name: null, employee_name: "", start_date: "", end_date: "", working_days: 0, note: "" },
  ]);

  const cancel = async () => {
    if (preview?.file_id) {
      try { await api.delete(`/meldungen/preview/${preview.file_id}`); } catch {}
    }
    reset();
    onOpenChange?.(false);
  };

  const save = async () => {
    const valid = rows.filter((r) => !r.__skip);
    const invalid = valid.filter((r) => !r.person_id || !r.start_date || !r.end_date);
    if (valid.length === 0) { toast.error("Toate rândurile sunt marcate skip"); return; }
    if (invalid.length > 0) {
      toast.error(`${invalid.length} rând(uri) cu date incomplete. Completează persoană/start/end sau bifează skip.`);
      return;
    }
    try {
      const body = {
        file_id: preview.file_id,
        storage_path: preview.storage_path,
        original_filename: preview.original_filename,
        content_type: preview.content_type,
        size: preview.size,
        extracted: preview.extracted,
        entries: valid.map((r) => ({
          person_id: r.person_id,
          employee_name: r.employee_name || null,
          start_date: r.start_date,
          end_date: r.end_date,
          working_days: Number(r.working_days) || null,
          note: r.note || null,
        })),
      };
      const { data } = await api.post("/meldungen/bulk-confirm", body);
      toast.success(`Salvate ${data.created} intrări${data.skipped ? ` (sărite: ${data.skipped})` : ""}`);
      reset();
      onOpenChange?.(false);
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="meldung-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <Upload size={18} /> Adaugă Meldeverfahren (multi-angajat)
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Încarcă documentul Meldung (PDF sau imagine). OCR-ul extrage toți angajații + intervalele detectate;
            tu confirmi alocările la persoanele din DB.
          </p>
        </DialogHeader>

        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/*"
          className="hidden"
          onChange={onFileChange}
          data-testid="meldung-bulk-file-input"
        />

        {!preview && (
          <div className="card-flat p-8 text-center space-y-3" data-testid="meldung-bulk-empty">
            <div className="inline-flex p-4 rounded-full bg-blue-50 text-blue-600">
              <FileText size={32} />
            </div>
            <div className="text-sm text-zinc-600">
              {uploading ? "Procesez documentul cu OCR…" : "Selectează un fișier pentru a începe"}
            </div>
            {!uploading && (
              <Button onClick={() => fileInputRef.current?.click()} data-testid="meldung-bulk-pick-file">
                <Upload size={16} className="mr-1" /> Alege fișier
              </Button>
            )}
          </div>
        )}

        {preview && (
          <>
            <div className="text-xs text-zinc-600 mb-2 inline-flex items-center gap-2">
              <FileText size={14} className="text-blue-600" />
              <span className="font-medium">{preview.original_filename}</span>
            </div>
            {preview.extract_error && (
              <div className="bg-red-50 border border-red-200 text-red-800 rounded p-2 text-xs inline-flex items-center gap-2 mb-3" data-testid="meldung-bulk-extract-error">
                <AlertTriangle size={14} /> OCR eșuat: {preview.extract_error}. Completează manual.
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="meldung-bulk-table">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                  <tr>
                    <th className="text-left px-2 py-2 w-8">#</th>
                    <th className="text-left px-2 py-2">Nume extras</th>
                    <th className="text-left px-2 py-2 min-w-[220px]">Persoană DB</th>
                    <th className="text-left px-2 py-2">Start</th>
                    <th className="text-left px-2 py-2">End</th>
                    <th className="text-left px-2 py-2">Zile</th>
                    <th className="text-left px-2 py-2">Notă</th>
                    <th className="text-left px-2 py-2 w-12">Skip</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rows.map((r, idx) => {
                    const skipped = r.__skip;
                    const noMatch = !r.person_id;
                    return (
                      <tr key={r.__key} className={`${skipped ? "opacity-40 bg-zinc-50" : ""} ${noMatch && !skipped ? "bg-amber-50" : ""}`} data-testid={`meldung-bulk-row-${idx}`}>
                        <td className="px-2 py-2 align-top text-zinc-500">{idx + 1}</td>
                        <td className="px-2 py-2 align-top">
                          <Input value={r.employee_name} onChange={(e) => updateRow(r.__key, "employee_name", e.target.value)} disabled={skipped} className="h-8" data-testid={`meldung-bulk-name-${idx}`} />
                        </td>
                        <td className="px-2 py-2 align-top">
                          <Select value={r.person_id || "__none__"} onValueChange={(v) => updateRow(r.__key, "person_id", v === "__none__" ? null : v)} disabled={skipped}>
                            <SelectTrigger className="h-8" data-testid={`meldung-bulk-person-${idx}`}>
                              <SelectValue placeholder={noMatch ? "⚠ Selectează persoana" : ""} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Niciuna (skip) —</SelectItem>
                              {personOptions.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.is_sub ? "🌍 " : ""}{p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {r.matched_person_name && r.person_id && (
                            <div className="text-[10px] text-emerald-700 mt-0.5">✓ Auto-potrivit</div>
                          )}
                          {noMatch && !skipped && (
                            <div className="text-[10px] text-amber-700 mt-0.5">⚠ Niciun match — alege manual sau skip</div>
                          )}
                        </td>
                        <td className="px-2 py-2 align-top">
                          <Input type="date" value={r.start_date} onChange={(e) => updateRow(r.__key, "start_date", e.target.value)} disabled={skipped} className="h-8 w-[140px]" data-testid={`meldung-bulk-start-${idx}`} />
                        </td>
                        <td className="px-2 py-2 align-top">
                          <Input type="date" value={r.end_date} onChange={(e) => updateRow(r.__key, "end_date", e.target.value)} disabled={skipped} className="h-8 w-[140px]" data-testid={`meldung-bulk-end-${idx}`} />
                        </td>
                        <td className="px-2 py-2 align-top">
                          <Input type="number" min={0} value={r.working_days} onChange={(e) => updateRow(r.__key, "working_days", e.target.value)} disabled={skipped} className="h-8 w-[70px]" data-testid={`meldung-bulk-days-${idx}`} />
                        </td>
                        <td className="px-2 py-2 align-top">
                          <Input value={r.note} onChange={(e) => updateRow(r.__key, "note", e.target.value)} disabled={skipped} className="h-8" placeholder="opțional" data-testid={`meldung-bulk-note-${idx}`} />
                        </td>
                        <td className="px-2 py-2 align-top text-center">
                          <Button size="sm" variant="ghost" onClick={() => toggleSkip(r.__key)} className={skipped ? "text-zinc-500" : "text-red-600 hover:text-red-700"} data-testid={`meldung-bulk-skip-${idx}`} title={skipped ? "Anulează skip" : "Sari peste"}>
                            <X size={14} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-3">
              <Button variant="outline" size="sm" onClick={addEmptyRow} data-testid="meldung-bulk-add-row">
                <Plus size={14} className="mr-1" /> Adaugă rând
              </Button>
              <div className="text-xs text-zinc-500">
                {rows.filter((r) => !r.__skip && r.person_id && r.start_date && r.end_date).length} rând(uri) valide / {rows.length} total
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={cancel} data-testid="meldung-bulk-cancel">Anulează</Button>
          {preview && (
            <Button onClick={save} data-testid="meldung-bulk-confirm">Salvează toate</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
